import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealMask } from "@/components/motion/Reveal";
import ScrambleText from "@/components/motion/ScrambleText";
import WorkCard from "@/components/WorkCard";
import { work } from "@/data/content";

function Heading() {
  return (
    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <ScrambleText as="p" text="Selected Work" className="mb-3 block text-xs md:mb-4 font-bold uppercase tracking-[0.22em] text-accent" />
        <RevealMask as="h2" className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-ink md:text-6xl">
          The work speaks
        </RevealMask>
        <RevealMask as="h2" delay={0.08} className="font-serif text-5xl font-normal italic leading-[0.95] text-muted md:text-6xl">
          first.
        </RevealMask>
      </div>
      <Link
        to="/work"
        data-testid="featured-view-all"
        className="group inline-flex items-center gap-2 border-b border-ink pb-1 text-sm font-bold uppercase tracking-[0.14em] text-ink"
      >
        View all case studies
        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  );
}

function HorizontalCard({ item }) {
  return (
    <Link
      to={`/work/${item.slug}`}
      data-testid={`work-card-h-${item.slug}`}
      data-cursor="view"
      className="group block"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-line">
        <img
          src={item.image}
          alt={`${item.client} — ${item.title}`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div className="pointer-events-none absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/20" />
      </div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl font-extrabold leading-tight text-ink transition-colors duration-300 group-hover:text-accent md:text-3xl">
            {item.client}
          </h3>
          <p className="mt-1 text-sm text-muted">{item.title}{item.year ? ` · ${item.year}` : ""}</p>
        </div>
        <span className="shrink-0 text-xs font-bold uppercase tracking-[0.14em] text-muted">{item.category}</span>
      </div>
    </Link>
  );
}

function HorizontalStrip({ items }) {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [range, setRange] = useState(1000);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        setRange(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 80));
      }
    };
    measure();
    const t = setTimeout(measure, 900);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  return (
    <div ref={sectionRef} data-testid="featured-horizontal" className="relative hidden lg:block" style={{ height: "300vh" }}>
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto mb-10 w-full max-w-[1600px] px-10 xl:mb-12 xl:px-16">
          <Heading />
        </div>
        <motion.div ref={trackRef} style={{ x }} className="flex items-stretch gap-[3vw] pl-10 will-change-transform xl:pl-16">
          {items.map((item) => (
            <div key={item.slug} className="w-[34vw] shrink-0">
              <HorizontalCard item={item} />
            </div>
          ))}
          <Link
            to="/work"
            data-testid="featured-horizontal-cta"
            className="group flex w-[24vw] shrink-0 flex-col items-start justify-center gap-6 border border-line bg-surface p-10 transition-colors duration-500 hover:bg-ink"
          >
            <span className="font-serif text-4xl italic text-accent">→</span>
            <span className="font-display text-3xl font-extrabold leading-tight text-ink transition-colors duration-500 group-hover:text-bone">
              See every
              <br />
              case study
            </span>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted transition-colors duration-500 group-hover:text-accent">
              All work <ArrowUpRight size={14} />
            </span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

export default function FeaturedWork() {
  const featured = work.slice(0, 4);
  return (
    <section data-testid="featured-work">
      <HorizontalStrip items={featured} />
      <div className="mx-auto max-w-[1600px] px-5 py-20 sm:py-24 md:py-28 lg:py-32 sm:px-8 md:px-10 lg:px-12 xl:px-16 lg:hidden">
        <div className="mb-10 sm:mb-14">
          <Heading />
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:gap-y-14 md:grid-cols-2">
          {featured.map((item, i) => (
            <WorkCard key={item.slug} item={item} index={i} className={i % 3 === 0 ? "md:mt-0" : "md:mt-12 lg:mt-16"} />
          ))}
        </div>
      </div>
    </section>
  );
}
